import { FC } from 'react';
import { Colors } from 'src/styles';
import { ConstraintViolation } from 'src/utils/is-constraint-violation';
import styled from 'styled-components';
import { setLocale } from 'yup';

const required = 'Required';
const defined = 'Defined';
const typeNumber = 'TypeNumber';
const typeDefault = 'TypeDefault';
const integer = 'IntergersOnly';

setLocale({
  mixed: {
    required,
    defined,
    notType(params) {
      if (params.type === 'number') return typeNumber;
      return typeDefault;
    },
  },
  number: {
    integer,
    min:
      ({ min }) =>
      (label: string) =>
        `${label} has to be at least ${min}.`,
  },
});

export const ErrorText = styled.small`
  color: ${Colors.error};
`;

// Violation messages from the server
// see https://prisma-capacity.gitlab.io/shipper-registration/{branch-name}/#_constraint_violation
type ViolationMessage =
  // The field exceeds the maximum value.
  | 'Max'
  | 'DecimalMax'
  // The field exceeds the minimum value.
  | 'Min'
  | 'DecimalMin'
  // The field should have more than one character which is not whitespace and is required.
  | 'NotBlank'
  // The field is not optional and not given in the request.
  | 'NotNull'
  // Too long.
  | 'Size'
  // The field has to match certain pattern.
  | 'Pattern'
  // The field has to be a positive number, not including 0.
  // | 'Positive'
  // The field has be an email.
  | 'Email'
  // The field is not a valid URL.
  | 'URL'
  // The provided IBAN number is not valid.
  | 'Iban'
  // The BIC number is not valid.
  | 'Bic'
  // The field is not a valid file name or refers to a not supported file type.
  | 'FileName'
  // The phone number is not valid.
  | 'PhoneNumber'
  // The provided value has too many numbers before or after the decimal point.
  | 'Digits'
  // When the surrender window for the runtime is already closed.
  | 'Runtime';

export class EnrichedError {
  message: string;
  serverError: ConstraintViolation;

  constructor(message: string, serverError: ConstraintViolation) {
    this.message = message;
    this.serverError = serverError;
  }
}

type Props = {
  error:
    | string
    | ViolationMessage
    | EnrichedError
    | ((label: string) => string);
  label: string;
  type?: 'textual' | 'selectable' | 'uploadable' | 'selectableArray';
  /**
   * @deprecated Please do not use this function anymore.
   *
   * Use this pattern for zod to create custom error messages.
   *
   * ```
   * const validation = z
   *   .number()
   *   .refine(
   *     (value) => value !== 17,
   *     registerCustomErrorMessage(`{label} can't be 17.`) // <-- THIS is the custom error message
   *   );
   * ```
   *
   * Use this pattern for yup to create custom error messages.
   *
   * ```
   * import { number } from 'yup';
   *
   * const validation = number
   *   .test(
   *     'No17',
   *     () => (label: string) => `${label} can't be 17.`, // <-- THIS is the custom error message
   *     (value, context) => value !== 17
   *   );
   * ```
   */
  getCustomErrorMessage?: (message: string) => string | undefined;
  'data-testid'?: string;
};

const plainErrorMessagesWhitelist = [
  PRISMA_CONFIG.capacityManagement,
  PRISMA_CONFIG.surrenderService,
  PRISMA_CONFIG.iipService,
  PRISMA_CONFIG.contractManagement,
  PRISMA_CONFIG.documentExchange,
  PRISMA_CONFIG.aggregateEu,
  PRISMA_CONFIG.authorizationService,
  PRISMA_CONFIG.organisationService,
];

function isServiceWhitelisted(url?: string) {
  if (!url) {
    return false;
  }

  return plainErrorMessagesWhitelist.some(
    (item) => item && url.startsWith(item)
  );
}

const errorPrefix = 'ERROR: ';

/**
 * This is a workaround to support custom zod validation messages (but can also be useful for yup).
 * The behaviour arround error message handling is a bit weird, because we had some requirements in the
 * past that are no longer valid.
 * If you want a custom message, it must be registered with this function.
 *
 * You can place "{label}" somewhere in your message in case you want to show the label of the field.
 */
export function registerCustomErrorMessage(message: string) {
  return errorPrefix + message;
}

export function getMessage({
  error,
  label,
  type = 'textual',
  getCustomErrorMessage,
}: Props) {
  label = label.replace('*', '');
  const message = error instanceof EnrichedError ? error.message : error;
  const customMessage = getCustomErrorMessage?.(
    message instanceof Function ? message(label) : message
  );

  // If there is an custom message use this message and skip the rest.
  if (customMessage) {
    return customMessage;
  }

  if (typeof message === 'string' && message.startsWith(errorPrefix)) {
    return message.replace(errorPrefix, '').replace('{label}', label);
  }

  /**
   * @deprecated Please do not add more error codes.
   *
   * Use this pattern for zod to create custom error messages.
   *
   * ```
   * const validation = z
   *   .number()
   *   .refine(
   *     (value) => value !== 17,
   *     registerCustomErrorMessage(`{label} can't be 17.`) // <-- THIS is the custom error message
   *   );
   * ```
   *
   * Use this pattern for yup to create custom error messages.
   *
   * ```
   * import { number } from 'yup';
   *
   * const validation = number
   *   .test(
   *     'No17',
   *     () => (label: string) => `${label} can't be 17.`, // <-- THIS is the custom error message
   *     (value, context) => value !== 17
   *   );
   * ```
   */
  switch (message) {
    case required:
    case defined:
    case 'NotBlank':
    case 'NotNull':
      if (type === 'textual') {
        return `Please enter a value for ${label}.`;
      } else if (type === 'uploadable') {
        return `Please upload a file for ${label}.`;
      } else {
        return `Please select a value for ${label}.`;
      }
    case 'TooEarly':
      return `Please enter a later date for ${label}.`;
    case 'TooLate':
      return `Please enter an earlier date for ${label}.`;
    case typeNumber:
      return `Please enter a valid number.`;
    case typeDefault:
      return `Please enter a valid type.`;
    case 'Accept':
      return `Please accept ${label}.`;
    case 'Confirm':
      return `Please confirm that ${label}.`;
    case 'PhoneNumber':
      return `Please enter a valid phone number for ${label}.`;
    case 'Invalid email format':
      return `Please enter a valid email address.`;
    case 'Invalid email':
    case 'Email':
      return `Please enter a valid email address for ${label}.`;
    case 'URL':
      return `Please enter a valid URL for ${label}.`;
    case 'Iban':
      return `Please enter a valid IBAN.`;
    case 'Bic':
      return `Please enter a valid BIC.`;
    case 'MinAndMaxIban':
      return `IBAN must contain between 5 and 34 characters.`;
    case 'MinAndMaxBic':
      return `BIC must contain between 8 and 11 characters.`;
    case 'Date':
      return `Please enter a valid date for ${label}.`;
    case 'FileName':
      return `Please enter a valid file name with a file extension for ${label}.`;
    case 'FileUploadPending':
      return 'File upload in progress, please wait until upload is finished.';
    case 'Size':
      return `Please shorten the text for ${label}.`;
    case 'Pattern':
      return `Please correct the format for ${label}.`;
    case 'DeactivateConfirmation':
      return `Please enter "DEACTIVATE" to be able to proceed.`;
    case 'EICNotFound':
      return 'The EIC is not known. Please check if it is registered correctly at ENTSO-E.';
    case 'EICFormat':
      return `Your EIC number is invalid.`;
    case 'EICCannotBeVerified':
      return `The EIC can't be verified currently. Please try it again later.`;
    case 'DVGWNotFound':
      return 'The DVGW code provided is not listed as a supplier code in the DVGW database.';
    case 'DVGWCannotBeVerified':
      return `The DVGW can't be verified currently. Please try it again later.`;
    case 'ConfirmPassword':
      return `Both passwords need to be the same.`;
    case 'DVGWFormat':
    case 'DVGWSizeInvalid':
      return `Your DVGW number is invalid. DVGW length is expected to equal 13 characters.`;
    case 'InvalidPassword':
      return `Please use a password which fulfills the complexity criteria.`;
    case 'InvalidVerificationCode':
      return `The provided verification code was not correct.`;
    case 'Max':
    case 'DecimalMax':
      return `The provided value is too big.`;
    case 'Digits':
      return `The provided value has too many numbers before or after the decimal point.`;
    case 'Min':
    case 'DecimalMin':
      return `The provided value is too low.`;
    case 'FSMinAmount':
      return 'Provided value needs to be 0 or at least 3,000.00 EUR.';
    case 'FSAssociateTotalMax':
      return 'You cannot associate more than total amount.';
    case 'MinItems':
      return `The field must have at least 1 item.`;
    case 'EmailAddressAlreadyUsed':
      return `The email address provided is already registered.`;
    case 'Runtime':
      return `The surrender window for this runtime is already closed.`;
    case 'NoRuntimeAvailable':
      return `Within the next 30 days there is no auction published for the product type you selected. Please select another product type.`;
    case integer:
      return `${label} should not contain decimals.`;
    case 'NotValidUnit':
      return `The previously selected ${label} is no longer valid. Please select a new unit.`;
    case 'MinAmendment':
      return 'The new amount cannot be lower than currently associated amount.';
    case 'MaxRelease':
      return 'The requested amount cannot be higher than the currently un-associated amount.';
    case 'DocumentNotFound':
      return 'The document was not found. Please, upload it again.';
    default:
      if (
        error instanceof EnrichedError &&
        isServiceWhitelisted(error.serverError.config.url)
      ) {
        // note: due to problems there was a discussion to *not* use error codes,
        // but human-readable errors in some cases in the capacity management api
        // this should be a *temporary* workaround (but we all know what that means right...?)
        // see https://prisma.atlassian.net/wiki/spaces/TAG/pages/1706066159/TAG0003+Error+Codes+for+Frontend#Outcome
        return error.message;
      }

      if (error instanceof Function) {
        return error(label);
      }

      if (error && typeof error === 'object') {
        return `An unexpected error happened for ${label}. We're sorry. (${JSON.stringify(
          error
        )})`;
      }

      return `An unexpected error happened for ${label}. We're sorry. (${error})`;
  }
}

export const ErrorMessage: FC<Props> = ({
  'data-testid': testId,
  ...props
}) => {
  return (
    <div>
      <ErrorText data-testid={testId}>{getMessage(props)}</ErrorText>
    </div>
  );
};
